import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { Button, ButtonGroup, Text, Tooltip } from "@chakra-ui/react";
import React from "react";
import { FiFeather, FiFileText } from "react-icons/fi";

/**
 * Application Row Actions Component
 * Displays action buttons (Save/Cancel or Resume/Cover Letter) based on row state
 * 
 * @param {Object} props
 * @param {Object} props.application - Application data
 * @param {boolean} props.hasChanges - Does row have unsaved changes
 * @param {boolean} props.isSaving - Is row currently saving
 * @param {boolean} props.isDisabled - Is row disabled (archived)
 * @param {boolean} props.canAccessResumeCoverLetter - Can user access resume/cover letter
 * @param {Function} props.onSave - Save handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {Function} props.onResumeClick - Resume button handler
 * @param {Function} props.onCoverLetterClick - Cover letter button handler
 */
const ApplicationRowActions = ({
  application,
  hasChanges,
  isSaving,
  isDisabled,
  canAccessResumeCoverLetter,
  onSave,
  onCancel,
  onResumeClick,
  onCoverLetterClick,
}) => {
  // Show Save/Cancel if there are changes
  if (hasChanges) {
    return (
      <ButtonGroup size="sm" variant="outline" isAttached>
        <Button
          colorScheme="green"
          leftIcon={<CheckIcon />}
          isLoading={isSaving}
          onClick={onSave}
          borderRadius="lg"
          _hover={{ transform: "translateY(-1px)" }}
          transition="all 0.2s ease"
          _focus={{ boxShadow: "none" }}
        >
          Save
        </Button>
        <Button
          colorScheme="red"
          leftIcon={<CloseIcon />}
          isDisabled={isSaving}
          onClick={onCancel}
          borderRadius="lg"
          _hover={{ transform: "translateY(-1px)" }}
          transition="all 0.2s ease"
          _focus={{ boxShadow: "none" }}
        >
          Cancel
        </Button>
      </ButtonGroup>
    );
  }

  // Not analyzed yet AND not a Magic Tailor app - no actions available
  if (!application.lastAnalyzedAt && !application.isMagicTailor) {
    return (
      <Text fontSize="xs" color="gray.400" fontStyle="italic">
        Configure first
      </Text>
    );
  }

  // Already analyzed - show Resume/Cover Letter buttons
  return (
    <ButtonGroup size="sm" variant="outline" isAttached>
      <Tooltip label="View or generate resume" placement="top" hasArrow>
        <Button
          colorScheme="blue"
          isDisabled={isDisabled}
          onClick={onResumeClick}
          leftIcon={<FiFileText />}
          borderRadius="lg"
          _hover={{
            transform: "translateY(-1px)",
            bg: "blue.50",
          }}
          transition="all 0.2s ease"
          _focus={{ boxShadow: "none" }}
          fontSize="xs"
        >
          Resume
        </Button>
      </Tooltip>

      <Tooltip label="View or generate cover letter" placement="top" hasArrow>
        <Button
          colorScheme="orange"
          isDisabled={isDisabled}
          onClick={onCoverLetterClick}
          leftIcon={<FiFeather />}
          borderRadius="lg"
          _hover={{
            transform: "translateY(-1px)",
            bg: "orange.50",
          }}
          transition="all 0.2s ease"
          _focus={{ boxShadow: "none" }}
          fontSize="xs"
        >
          Cover Letter
        </Button>
      </Tooltip>
    </ButtonGroup>
  );
};

export default ApplicationRowActions;
